import axios from 'axios';
import request from 'store/utils/request';
import _get from 'lodash/get';
import _has from 'lodash/has';

import MESSAGES from 'constants/messages';

import ErrorHandler from 'store/utils/ErrorHandler';
import Notify from 'store/utils/Notify';
import { getUser } from 'store/services/session/selectors';

import * as api from './api';
import * as types from './actionTypes';
import * as selectors from './selectors';

/**
 * Check if a project already exists for an account
 *
 * @param name
 * @param account
 * @param idNotIn
 * @returns {Function}
 */
export const checkProjectName = (name, account, idNotIn) => async () => {
  try {
    const { data } = await api.checkProjectNameRequest(name, account, idNotIn);
    if (data.count > 0) {
      // eslint-disable-next-line no-throw-literal
      throw {
        name: MESSAGES.PROJECTS.NAME.EXISTS,
      };
    }
  } catch (err) {
    if (_has(err, 'name')) throw err;
  }
};

export const GET_PROJECTS_ALL_REQUEST_ID = 'get-projects-all';
/**
 * Get all projects
 */
export const getProjectsAll = (params = {}) => async dispatch => {
  try {
    dispatch({
      type: types.GET_ALL_PROJECTS_REQUEST,
    });

    const { data } = await api.getProjectsRequest(
      {
        ...params,
        ordering: 'name',
      },
      GET_PROJECTS_ALL_REQUEST_ID,
    );

    dispatch({
      type: types.GET_ALL_PROJECTS_SUCCESS,
      payload: {
        projects: data.results,
        count: data.count,
      },
    });
  } catch (error) {
    if (axios.isCancel(error)) return;

    dispatch({
      type: types.GET_ALL_PROJECTS_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Cancel getProjectAll request
 */
export const cancelGetProjectsAll = () => dispatch => {
  request.cancel(GET_PROJECTS_ALL_REQUEST_ID);

  dispatch({
    type: types.CANCEL_GET_ALL_PROJECTS,
  });
};

/**
 * Get paginated projects
 *
 * @param params
 */

export const getProjectsList = (params = {}) => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.GET_PROJECTS_REQUEST,
    });
    const finalParams = {
      ...selectors.getPaginationOptions(getState()),
      ...params,
    };

    await dispatch({
      type: types.UPDATE_PROJECTS_PAGINATION_PARAMS,
      payload: {
        paginationOptions: finalParams,
      },
    });

    const { data } = await api.getProjectsRequest(finalParams);

    await dispatch({
      type: types.GET_PROJECTS_SUCCESS,
      payload: {
        projects: data.results,
        count: data.count,
      },
    });
  } catch (error) {
    await dispatch({
      type: types.GET_PROJECTS_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Add a project
 *
 * @param data
 */

export const addProject = ({ name, account, template, flow2 }) => async (
  dispatch,
  getState,
) => {
  try {
    await dispatch({ type: types.ADD_PROJECT_REQUEST });

    const data = {
      name,
      account,
    };

    if (template) {
      data.project_template = template;
    }

    if (flow2) {
      data.dialog_flow_v2_enabled = flow2;
    }

    const userAccountSlug = getUser(getState()).account_slug;

    await api.addProjectRequest(userAccountSlug, data);

    dispatch({ type: types.ADD_PROJECT_SUCCESS });

    const finalParams = {
      ...selectors.getPaginationOptions(getState()),
      page: 1,
    };

    dispatch({
      type: types.UPDATE_PROJECTS_PAGINATION_PARAMS,
      payload: {
        paginationOptions: finalParams,
      },
    });

    const response = await api.getProjectsRequest(finalParams);

    dispatch({
      type: types.GET_PROJECTS_SUCCESS,
      payload: {
        projects: response.data.results,
        count: response.data.count,
      },
    });

    Notify.success({ title: 'Project was created successfully' });
  } catch (error) {
    dispatch({
      type: types.ADD_PROJECT_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to create Project',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Import a project
 *
 * @param data
 */
export const importProject = data => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.IMPORT_PROJECT_REQUEST,
    });

    const userAccountSlug = getUser(getState()).account_slug;
    await api.importProjectRequest(userAccountSlug, data);

    await dispatch({
      type: types.IMPORT_PROJECT_SUCCESS,
    });

    const finalParams = {
      ...selectors.getPaginationOptions(getState()),
      page: 1,
    };

    await dispatch({
      type: types.UPDATE_PROJECTS_PAGINATION_PARAMS,
      payload: {
        paginationOptions: finalParams,
      },
    });

    const response = await api.getProjectsRequest(finalParams);

    await dispatch({
      type: types.GET_PROJECTS_SUCCESS,
      payload: {
        projects: response.data.results,
        count: response.data.count,
      },
    });

    Notify.success({
      title: 'Project was created successfully',
    });
  } catch (error) {
    await dispatch({
      type: types.IMPORT_PROJECT_ERROR,
      payload: {
        error: error.message,
      },
    });

    const errorMsg = 'Something went wrong while importing this project';

    if (error.response) {
      const { data, status } = error.response;

      Notify.error({
        title: 'An error occurred',
        description: ErrorHandler.parseErrors(
          _get(data, 'detail', errorMsg),
          status,
        ),
      });
    } else {
      Notify.error({
        title: 'An error occurred',
        description: errorMsg,
      });
    }
  }
};

/**
 * Clone a project
 *
 * @param projectAccount
 * @param projectSlug
 * @param data
 * @returns {Function}
 */
export const cloneProject = (projectAccount, projectSlug, data) => async (
  dispatch,
  getState,
) => {
  try {
    await dispatch({
      type: types.CLONE_PROJECT_REQUEST,
    });

    await api.cloneProjectRequest(projectAccount, projectSlug, data);

    await dispatch({
      type: types.CLONE_PROJECT_SUCCESS,
    });

    const finalParams = {
      ...selectors.getPaginationOptions(getState()),
      page: 1,
    };

    await dispatch({
      type: types.UPDATE_PROJECTS_PAGINATION_PARAMS,
      payload: {
        paginationOptions: finalParams,
      },
    });

    const response = await api.getProjectsRequest(finalParams);

    await dispatch({
      type: types.GET_PROJECTS_SUCCESS,
      payload: {
        projects: response.data.results,
        count: response.data.count,
      },
    });

    Notify.success({
      title: 'Project was created successfully',
    });
  } catch (error) {
    await dispatch({
      type: types.CLONE_PROJECT_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Toggle projects list appearance
 *
 * @param isGrid
 */
export const toggleProjectsGrid = (isGrid = false) => ({
  type: types.TOGGLE_PROJECTS_GRID,
  payload: {
    isGrid,
  },
});
